import { createFileRoute } from "@tanstack/react-router";
import { PageHero } from "@/components/site/PageHero";
import { Link } from "@tanstack/react-router";
import { SITE } from "@/config/site";
import { Phone, Mail, MessageCircle, MapPin, Facebook, Instagram, Clock, Star } from "lucide-react";
import contactCleanerAssetUrl from "@/assets/izhub-cleaner-contact.webp";
const contactCleanerAsset = { url: contactCleanerAssetUrl };

export const Route = createFileRoute("/contact")({
  head: () => ({
    meta: [
      { title: "Contact IZHUB Cleaning Service — Call, Email or WhatsApp" },
      { name: "description", content: "Contact IZHUB Cleaning Service, Talavera Way, Tidworth SP9 7WH. Open 8am–6pm, Monday to Saturday. Phone, email or WhatsApp — covering Tidworth, Andover, Salisbury and Salisbury Plain." },
      { property: "og:title", content: "Contact IZHUB Cleaning Service — Call, Email or WhatsApp" },
      { property: "og:description", content: "Call, WhatsApp, email or send an enquiry. Cleaners covering Tidworth, Andover, Salisbury and Salisbury Plain." },
      { property: "og:url", content: "https://izhubcleaning.uk/contact" },
    ],
    links: [{ rel: "canonical", href: "https://izhubcleaning.uk/contact" }],
  }),
  component: Contact,
});

function Contact() {
  return (
    <>
      <PageHero
        title={<>Let's plan your <span className="rainbow-text">next clean</span>.</>}
        intro="Call, WhatsApp, email, or use the enquiry form below. We usually reply within 2 hours during business hours."
        withBackground
      />

      <section className="py-16 md:py-20">
        <div className="container-x grid gap-10 lg:grid-cols-[1fr_1.4fr]">
          <div className="space-y-4">
            <ContactCard icon={Phone} label="Call us" value="07763 933 920" href="tel:+447763933920" />
            <ContactCard icon={MessageCircle} label="WhatsApp Us" value="Message on WhatsApp" href={SITE.whatsapp} />
            <ContactCard icon={Mail} label="Email us" value="info@izhubcleaning.uk" href="mailto:info@izhubcleaning.uk" />
            <div className="rounded-3xl border border-border bg-card p-5">
              <div className="flex items-center gap-3">
                <span className="grid h-10 w-10 place-items-center rounded-2xl bg-sage-soft text-primary"><MapPin className="h-4 w-4" /></span>
                <p className="text-sm font-medium">Our address</p>
              </div>
              <address className="mt-3 not-italic text-sm text-foreground/90">
                IZHUB Cleaning Service<br />
                {SITE.addressStreet}<br />
                {SITE.addressTown}, {SITE.addressPostcode}<br />
                United Kingdom
              </address>
              <p className="mt-3 text-sm text-muted-foreground">
                We cover Tidworth, Tidworth Garrison, Perham Down, Shipton Bellinger, Ludgershall, Bulford, Larkhill, Amesbury, Collingbourne, Andover, Salisbury and the wider Salisbury Plain area (SP9, SP4, SP11, SP1–SP5, SP10).
              </p>
            </div>

            <div className="rounded-3xl border border-border bg-card p-5">
              <div className="flex items-center gap-3">
                <span className="grid h-10 w-10 place-items-center rounded-2xl bg-sage-soft text-primary"><Clock className="h-4 w-4" /></span>
                <p className="text-sm font-medium">Opening &amp; response hours</p>
              </div>
              <ul className="mt-3 space-y-1.5 text-sm text-foreground/90">
                <li className="flex justify-between gap-4"><span>Monday – Friday</span><span className="font-medium">8:00am – 6:00pm</span></li>
                <li className="flex justify-between gap-4"><span>Saturday</span><span className="font-medium">8:00am – 6:00pm</span></li>
                
              </ul>
              <p className="mt-3 text-sm text-muted-foreground">
                Calls, WhatsApp messages and emails received during these hours are usually answered within 2 hours. Messages sent after 6:00pm are answered the next morning.
              </p>
            </div>

            <div className="rounded-3xl border border-border bg-card p-5">
              <div className="flex items-center gap-3">
                <span className="grid h-10 w-10 place-items-center rounded-2xl bg-sage-soft text-primary"><Star className="h-4 w-4" /></span>
                <p className="text-sm font-medium">Find us on Google</p>
              </div>
              <p className="mt-3 text-sm text-muted-foreground">
                See our location, photos and customer reviews on our Google Business Profile.
              </p>
              <div className="mt-4 overflow-hidden rounded-2xl border border-border">
                <iframe
                  title="Map showing IZHUB Cleaning Service at Talavera Way, Tidworth SP9 7WH"
                  src={`https://www.google.com/maps?q=${SITE.mapsEmbedQuery}&output=embed`}
                  width="100%"
                  height="240"
                  style={{ border: 0 }}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
              <div className="mt-4 flex flex-wrap gap-3">
                <a href={SITE.googleBusinessUrl} target="_blank" rel="noreferrer" className="btn-outline">
                  View our Google listing
                </a>
                <a href={SITE.googleReviewUrl} target="_blank" rel="noreferrer" className="btn-outline">
                  Leave a Google review
                </a>
              </div>
            </div>

            <div className="overflow-hidden rounded-3xl border border-border bg-card">
              <img
                src={contactCleanerAsset.url}
                alt="IZHUB Cleaning Service professional ready to help"
                className="h-auto w-full object-contain"
                loading="lazy"
              />
            </div>

            <div className="rounded-3xl border border-border bg-card p-5">
              <h3 className="font-display text-lg">Follow IZHUB Cleaning Services</h3>
              <p className="mt-2 text-sm text-muted-foreground">Follow us on Facebook and Instagram to see recent cleans, updates, availability and customer feedback.</p>
              <div className="mt-4 flex flex-wrap gap-3">
                <a
                  href={SITE.facebook}
                  target="_blank"
                  rel="noreferrer"
                  aria-label="Visit IZHUB Cleaning Services on Facebook"
                  className="inline-flex items-center gap-2 rounded-full border border-border bg-background px-4 py-2.5 text-sm font-medium text-foreground/80 hover:text-primary hover:border-primary transition-colors"
                >
                  <Facebook className="h-4 w-4" /> Facebook
                </a>
                <a
                  href={SITE.instagram}
                  target="_blank"
                  rel="noreferrer"
                  aria-label="Visit IZHUB Cleaning Services on Instagram"
                  className="inline-flex items-center gap-2 rounded-full border border-border bg-background px-4 py-2.5 text-sm font-medium text-foreground/80 hover:text-primary hover:border-primary transition-colors"
                >
                  <Instagram className="h-4 w-4" /> Instagram
                </a>
              </div>
            </div>
          </div>

          <div className="rounded-3xl border border-border bg-card p-6 md:p-8 shadow-soft h-fit">
            <p className="eyebrow">Instant quote</p>
            <h2 className="mt-3 font-display text-2xl md:text-3xl">Get your price in seconds.</h2>
            <p className="mt-3 text-sm text-muted-foreground">
              Prefer to see the price before you send anything? Use our instant quote calculator — pick your service, tell us about your property, and see your estimate right away.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/quote" className="btn-primary">Get my instant quote</Link>
              <a href={SITE.whatsapp} target="_blank" rel="noreferrer" className="btn-whatsapp">
                <MessageCircle className="h-4 w-4" /> WhatsApp Us
              </a>
            </div>
            <p className="mt-6 text-xs text-muted-foreground">
              Or email <a href="mailto:info@izhubcleaning.uk" className="underline underline-offset-4">info@izhubcleaning.uk</a> — we usually reply within 2 hours during business hours.
            </p>
          </div>
        </div>
      </section>
    </>
  );
}

function ContactCard({ icon: Icon, label, value, href }: { icon: typeof Phone; label: string; value: string; href: string }) {
  return (
    <a href={href} target={href.startsWith("http") ? "_blank" : undefined} rel="noreferrer" className="flex items-center gap-4 rounded-3xl border border-border bg-card p-5 hover:border-primary transition-colors">
      <span className="grid h-11 w-11 place-items-center rounded-2xl bg-sage-soft text-primary"><Icon className="h-5 w-5" /></span>
      <div>
        <p className="text-xs uppercase tracking-wider text-muted-foreground">{label}</p>
        <p className="font-display text-lg">{value}</p>
      </div>
    </a>
  );
}
